// Centrale Actielijst (PLAN-P15 D3) — the single prioritised "what to do next" list, rolled up across
// every aggregate (ticket / risico / security-measure / sprint-gate / change-trigger). Read-only; served
// by the tenant-worker GET /actielijst (tenant-filtered SECURITY DEFINER fn). Source: 9-SPRINTS-METHODOLOGY §2.8.
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { PageHeader } from "@/components/page-header";
import { DataTable, type Column } from "@/components/data-table";
import { Badge } from "@/components/ui/badge";

interface ActielijstRow extends Record<string, unknown> {
  sourceKind: string; sourceId: string; title: string | null; lifecycle: string | null;
  priorityScore: number | null; dueAt: string | null; overdue: boolean;
}

// Per-source-kind badge tint (kept stable across the rebuild; mapped onto Tailwind tokens).
const KIND_CLASS: Record<string, string> = {
  ticket: "bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300",
  risico: "bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300",
  security_measure_instance: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
  sprint_gate_decision: "bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300",
  change_trigger_event: "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
};

export function ActielijstRoute() {
  const { t, lang } = useI18n();
  const { data, isLoading, error } = useQuery({
    queryKey: ["actielijst"],
    queryFn: () => api.listActielijst() as Promise<ActielijstRow[]>,
  });
  const rows = data ?? [];
  const fmtDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(lang === "nl" ? "nl-NL" : "en-GB", { year: "numeric", month: "short", day: "numeric" }) : "—";

  const columns: Column<ActielijstRow>[] = [
    {
      key: "source", header: t("al.source"),
      render: (r) => <Badge className={KIND_CLASS[r.sourceKind] ?? "bg-muted text-muted-foreground"}>{t(`al.kind.${r.sourceKind}`)}</Badge>,
    },
    { key: "item", header: t("al.item"), render: (r) => r.title ?? <span className="text-muted-foreground">—</span> },
    { key: "status", header: t("al.status"), render: (r) => r.lifecycle ?? "—" },
    { key: "priority", header: t("al.priority"), align: "right", render: (r) => <span className="tabular-nums">{r.priorityScore ?? "—"}</span> },
    {
      key: "due", header: t("al.due"), align: "right", className: "whitespace-nowrap",
      render: (r) => (
        <span className="inline-flex items-center justify-end gap-2">
          {fmtDate(r.dueAt)}
          {r.overdue && <Badge variant="destructive">{t("al.overdue")}</Badge>}
        </span>
      ),
    },
  ];

  return (
    <section>
      <PageHeader title={t("page.actielijst")} description={t("page.actielijst.sub")} />

      {error && <p className="mb-4 text-sm text-destructive">{(error as Error).message}</p>}

      <DataTable<ActielijstRow>
        columns={columns}
        rows={rows}
        loading={isLoading}
        getRowId={(r) => `${r.sourceKind}:${r.sourceId}`}
        empty={t("al.empty")}
      />
    </section>
  );
}
